import React, { useState } from 'react';
import Select, { StylesConfig } from 'react-select';
import TimeInput24 from './TimeInput24';
import { API_URLS } from '../../../config/Constants';
import { sessionManager } from '../../../config/sessionManager';
import api from '../../../config/axiosConfig';
import { showErrorMsg } from '../../../config/common';
import ShotLocationPicker, { PickedLocation } from '../../../components/shotChart/ShotLocationPicker';
import { LOCATABLE_SHOT_TYPES, ShotOutcome, SHOT_TYPE_TO_OUTCOME, buildShotLocation } from '../../../utils/shotLocation';
interface ActivePenalty {
  id: string;
  statId: string;
  time: string;
  openedInPeriod: string;
  periodLabel?: string | null;
  isCarriedOver?: boolean;
  playerId?: string;
}

interface AddStatModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  players: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    no?: number | string;
    position?: string[];
  }>;
  statTypes: string[];
  period: string;
  usPenalties?: ActivePenalty[];
  themPenalties?: ActivePenalty[];
}

interface OptionType {
  value: string;
  label: string;
}

// Styles that render the menu outside the modal via menuPortalTarget={document.body}
const portalSelectStyles: StylesConfig<OptionType, false> = {
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#fff',
    color: '#000',
  }),
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#fff',
    color: '#000',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(59,130,246,0.3)' : 'none',
    minHeight: '38px', // comfortable tap target
    fontSize: '16px', // prevents iOS zoom-on-focus
    fontWeight: '500',
    borderRadius: '8px',
  }),
  singleValue: (provided) => ({ ...provided, color: '#111827' }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#dbeafe' : state.isFocused ? '#f3f4f6' : '#fff',
    color: '#111827',
    fontSize: '16px',
    padding: '6px 8px', // larger tap area in dropdown list
  }),
  dropdownIndicator: (provided) => ({ ...provided, color: '#6b7280' }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: '#e5e7eb',
  }),
  input: (provided) => ({ ...provided, color: '#111827', fontSize: '16px' }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '16px',
    color: '#9ca3af',
  }),
};

const AddStatModal: React.FC<AddStatModalProps> = ({
  open,
  onClose,
  onSuccess,
  players,
  statTypes,
  period,
  usPenalties = [],
  themPenalties = [],
}) => {
  const now = new Date();
  const padTwo = (n: number) => String(n).padStart(2, '0');
  const todayStr = `${now.getFullYear()}-${padTwo(now.getMonth() + 1)}-${padTwo(now.getDate())}`;
  const nowTimeStr = `${padTwo(now.getHours())}:${padTwo(now.getMinutes())}:${padTwo(now.getSeconds())}`;

  const [form, setForm] = useState({
    date: todayStr,
    timeStr: nowTimeStr,
    statstype: '',
    playerId: '',
    player: '',
    possessionTime: '',
  });

  const [selectedThemPenaltyId, setSelectedThemPenaltyId] = useState<string>('');
  const [shotLocationValue, setShotLocationValue] = useState<PickedLocation | null>(null);

  const activeUsPenaltyPlayerIds = new Set(usPenalties.map((p) => p.playerId).filter(Boolean));

  const sideOptions: OptionType[] = [
    { value: 'us', label: 'US' },
    { value: 'them', label: 'THEM' },
  ];

  const usOnlyTypes = new Set(['Faceoff Lost', 'Was Blocked By', 'Shot Was Saved By', 'Shot not saved']);

  const getTypeOptionsForSide = (side: string) => {
    if (!side) return [];
    if (side === 'us') return statTypes;
    return statTypes.filter((type) => !usOnlyTypes.has(type));
  };

  const shouldShowPlayerDropdown = (statType: string, side: string) => {
    if (!statType || side !== 'us') return false;
    switch (statType) {
      case 'Penalty Start':
      case 'Penalty End':
      case 'Assist':
      case 'Shot Missed':
      case 'Shot Blocked':
      case 'Ground Ball':
      case 'Turnover':
      case 'Shot Saved':
      case 'Shot Was Saved By':
      case 'Shot Goal':
      case 'Shot not saved':
      case 'Was Blocked By':
      case 'Forced Turnover':
      case 'Caused Turnover':
      case 'Faceoff Won':
      case 'Faceoff Lost':
        return true;
      default:
        return false;
    }
  };

  const getFilteredPlayers = (statType: string, side: string) => {
    if (side !== 'us') return [];
    switch (statType) {
      case 'Penalty Start':
        return players;
      case 'Penalty End':
        return players.filter((p: any) => activeUsPenaltyPlayerIds.has(p._id));
      case 'Assist':
      case 'Shot Missed':
      case 'Shot Blocked':
      case 'Ground Ball':
      case 'Turnover':
      case 'Forced Turnover':
      case 'Caused Turnover':
      case 'Was Blocked By':
      case 'Shot Goal':
      case 'Shot Saved':
        return players;
      case 'Shot Was Saved By':
      case 'Shot not saved':
        return players.filter(
          (p: any) => Array.isArray(p.position) && p.position.some((pos: string) => pos === 'Goalie'),
        );
      case 'Faceoff Won':
      case 'Faceoff Lost':
        return players.filter(
          (p: any) => Array.isArray(p.position) && p.position.some((pos: string) => pos === 'Face-off'),
        );
      default:
        return players;
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const clientId = sessionManager.getClientId();
    const seasonId = sessionManager.getSeasonId();
    const gameId = sessionManager.getGameId();

    if (!clientId) {
      showErrorMsg('Please select a Client');
      return;
    } else if (!seasonId) {
      showErrorMsg('Please select a Season');
      return;
    } else if (!gameId) {
      showErrorMsg('Please select a Game');
      return;
    }

    if (!form.date || !form.timeStr || !form.statstype || !form.player) {
      showErrorMsg('Please fill in all required fields');
      return;
    }

    if (shouldShowPlayerDropdown(form.statstype, form.player) && !form.playerId) {
      showErrorMsg('Please select a player');
      return;
    }

    const needsThemPenaltyStart = form.player === 'them' && form.statstype === 'Penalty End';
    if (needsThemPenaltyStart && !selectedThemPenaltyId) {
      showErrorMsg('Please select which Them penalty to end');
      return;
    }

    if (form.statstype === 'Possession' && !form.possessionTime) {
      showErrorMsg('Please enter possession time');
      return;
    }

    let type = form.statstype;
    let timeValue = undefined;
    if (type === 'Possession' && form.possessionTime) {
      type = `Possession ${form.possessionTime}`;
      timeValue = form.possessionTime;
    }

    const combinedTime = new Date(`${form.date}T${form.timeStr}`);
    const payload: any = {
      time: combinedTime.toISOString(),
      period: period,
      type: type,
      player: form.player,
      clientId: clientId,
      seasonId: seasonId,
      gameId: gameId,
      updatestats: true,
    };

    if (form.player === 'us' && form.playerId) {
      payload.playerId = form.playerId;
    }

    if (form.player === 'them' && form.statstype === 'Penalty End' && selectedThemPenaltyId) {
      payload.penaltyStartId = selectedThemPenaltyId;
    }

    if ((form.player === 'us' || form.player === 'them') && LOCATABLE_SHOT_TYPES.includes(type) && shotLocationValue) {
      payload.shotLocation = buildShotLocation(shotLocationValue.x, shotLocationValue.y, 'postgame');
    }

    if (timeValue) payload.timeValue = timeValue;

    try {
      await api.post(API_URLS.ADD_STATS, payload);
      onSuccess();
      onClose();
      const resetNow = new Date();
      setForm({
        date: `${resetNow.getFullYear()}-${padTwo(resetNow.getMonth() + 1)}-${padTwo(resetNow.getDate())}`,
        timeStr: `${padTwo(resetNow.getHours())}:${padTwo(resetNow.getMinutes())}:${padTwo(resetNow.getSeconds())}`,
        statstype: '',
        playerId: '',
        player: '',
        possessionTime: '',
      });
      setSelectedThemPenaltyId('');
      setShotLocationValue(null);
    } catch (err: any) {
      console.error('Error adding stat:', err);
      if (err.response?.data?.message) {
        showErrorMsg(err.response.data.message);
      } else {
        showErrorMsg('Failed to add stat. Please try again.');
      }
    }
  };

  if (!open) return null;

  const showPlayer = shouldShowPlayerDropdown(form.statstype, form.player);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={onClose}>
      {/* Sheet slides up from bottom on mobile, centered dialog on desktop */}
      <div
        className="
          bg-white w-full sm:w-[480px] sm:max-w-[96vw]
          rounded-t-2xl sm:rounded-2xl
          shadow-2xl
          max-h-[92dvh] overflow-y-auto
          px-5 pt-5 pb-8
          flex flex-col gap-2
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile only) */}
        <div className="flex justify-center sm:hidden mb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Add New Stat</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {/* Date + Time row */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-gray-700">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleFormChange}
              className="border border-gray-300 rounded-lg text-base w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={{ height: '38px' }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-gray-700">
              Time <span className="text-red-500">*</span>
            </label>
            <TimeInput24
              value={form.timeStr}
              onChange={(val) => setForm((prev) => ({ ...prev, timeStr: val }))}
              className="w-full"
            />
          </div>
        </div>

        {/* Side */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-gray-700">
            Side <span className="text-red-500">*</span>
          </label>
          <Select
            options={sideOptions}
            value={sideOptions.find((opt) => opt.value === form.player) || null}
            onChange={(option) => {
              const newSide = option ? option.value : '';
              setForm((prev) => ({
                ...prev,
                player: newSide,
                statstype: '',
                playerId: '',
                possessionTime: '',
              }));
              setSelectedThemPenaltyId('');
              setShotLocationValue(null);
            }}
            placeholder="Select Side"
            styles={portalSelectStyles}
            isSearchable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        {/* Stat Type */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-gray-700">
            Stat Type <span className="text-red-500">*</span>
          </label>
          <Select
            options={getTypeOptionsForSide(form.player).map((type) => ({
              value: type,
              label: type,
            }))}
            value={
              getTypeOptionsForSide(form.player)
                .map((type) => ({ value: type, label: type }))
                .find((opt) => opt.value === form.statstype) || null
            }
            onChange={(option) => {
              const newStatType = option ? option.value : '';
              setForm((prev) => ({
                ...prev,
                statstype: newStatType,
                playerId: '',
                possessionTime: newStatType === 'Possession' ? prev.possessionTime : '',
              }));
              setSelectedThemPenaltyId('');
              setShotLocationValue(null);
            }}
            placeholder="Select Stat Type"
            isClearable
            styles={portalSelectStyles}
            isDisabled={!form.player}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          {form.statstype === 'Possession' && (
            <input
              type="number"
              step="0.1"
              name="possessionTime"
              className="border border-gray-300 rounded-lg px-3 py-2.5 text-base w-full mt-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter Possession Time (seconds)"
              value={form.possessionTime}
              onChange={handleFormChange}
              required
            />
          )}
        </div>

        {/* Shot Location — optional, shot-type stats for either side */}
        {(form.player === 'us' || form.player === 'them') &&
          LOCATABLE_SHOT_TYPES.includes(form.statstype) && (
          <div className="flex flex-col gap-1 border border-[#bccedf] rounded-lg p-3">
            <ShotLocationPicker
              outcome={(SHOT_TYPE_TO_OUTCOME[form.statstype] as ShotOutcome) || null}
              value={shotLocationValue}
              onChange={setShotLocationValue}
              title="Where was the shot taken? (optional)"
            />
          </div>
        )}

        {/* Them Penalty to End */}
        {form.player === 'them' && form.statstype === 'Penalty End' && (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-gray-700">
              Penalty to End <span className="text-red-500">*</span>
            </label>
            {themPenalties.length === 0 ? (
              <p className="text-sm text-red-500">No active Them penalties found.</p>
            ) : (
              <Select
                options={themPenalties.map((p) => ({
                  value: p.id,
                  label: `Them penalty started at ${p.time}${p.periodLabel ? ` ${p.periodLabel}` : ''}`,
                }))}
                value={
                  themPenalties
                    .map((p) => ({
                      value: p.id,
                      label: `Them penalty started at ${p.time}${p.periodLabel ? ` ${p.periodLabel}` : ''}`,
                    }))
                    .find((opt) => opt.value === selectedThemPenaltyId) || null
                }
                onChange={(option) => setSelectedThemPenaltyId(option ? option.value : '')}
                placeholder="Select penalty to end"
                isClearable
                styles={portalSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          </div>
        )}

        {/* Player */}
        {showPlayer && (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-gray-700">
              Player <span className="text-red-500">*</span>
            </label>
            <Select
              options={getFilteredPlayers(form.statstype, form.player).map((p) => ({
                value: p._id,
                label: `${p.no ? `#${p.no} ` : ''}${p.firstName} ${p.lastName}`,
              }))}
              value={
                getFilteredPlayers(form.statstype, form.player)
                  .map((p) => ({
                    value: p._id,
                    label: `${p.no ? `#${p.no} ` : ''}${p.firstName} ${p.lastName}`,
                  }))
                  .find((opt) => opt.value === form.playerId) || null
              }
              onChange={(option) => {
                setForm((prev) => ({
                  ...prev,
                  playerId: option ? option.value : '',
                }));
              }}
              placeholder="Select Player"
              isClearable
              styles={portalSelectStyles}
              isDisabled={!form.statstype}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-2">
          <button
            type="button"
            className="flex-1 py-2 rounded-xl border border-gray-300 text-gray-700 font-semibold text-base hover:bg-gray-50 active:bg-gray-100 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="flex-1 py-2 rounded-xl bg-blue-600 text-white font-semibold text-base hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50"
            onClick={handleSubmit}
          >
            Add Stat
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddStatModal;
